<script lang="ts" setup>
import { provide, ref, nextTick} from 'vue'
import { LocaleCodeKey } from '../../../utils/i18n'
import type { LocaleCode } from '../../../utils/i18n'
import { useI18n } from 'vue-i18n'
import type { Title, Locale } from './Title.vue'
import TitleForm from './Title.vue'
import ReleaseForm from './Release.vue'
import type { ReleaseSeason, SoftwareFormat, DevelopmentState, ReleaseDateAnnounce, Release } from './Release.vue'
import CompanyForm from './Company.vue'
import type { Company } from './Company.vue'
import MaturityRatingForm from './MaturityRating.vue'
import type { MaturityRatingSystem, MaturityRating } from './MaturityRating.vue'
import type { GameMaturityRating } from './MaturityRating.vue'
import _ from 'lodash'
import { put } from '@rails/request.js'

type Game = {
  id: number,
  titles: Title[],
  developers: Company[],
  publishers: Company[],
  maturity_rating_ids: number[],
  business_model_id: number,
  play_mode_ids: number[],
  release: Release
}

type PlayMode = {
  id: number,
  name: string,
  name_i18n: string
}

type BusinessModel = {
  id: number,
  name: string,
  name_i18n: string
}

type Errors = {
  titles: string[],
  game_maturity_ratings: string[]
}

const errors = ref<Errors>({ titles: [], game_maturity_ratings: []})

const props = defineProps<{
  locale: LocaleCode,
  data: {
    game: Game,
    constants: {
      locales: Locale[],
      development_states: DevelopmentState[],
      release_date_announces: ReleaseDateAnnounce[],
      release_seasons: ReleaseSeason[],
      software_formats: SoftwareFormat[],
      play_modes: PlayMode[],
      maturity_rating_systems: MaturityRatingSystem[],
      maturity_ratings: MaturityRating[],
      business_models: BusinessModel[],
    },
    paths: {
      update_game_path: string
    }
  }
}>()
const { locale, data: { constants } } = props

const { t } = useI18n({ locale })
provide(LocaleCodeKey, locale)

const game = ref(props.data.game)
const titles = ref<Title[]>(game.value.titles.map((t) => ({...t, component_id: Math.random()})))

const addTitle = () => {
  titles.value.push({ text: null, locale: null, component_id: Math.random() })
}

const deleteTitle = (component_id: number) => {
  titles.value.splice(
    _.findIndex(titles.value, { 'component_id': component_id }),
    1
  )
}

const game_maturity_ratings = ref<GameMaturityRating[]>(
  game.value.maturity_rating_ids.map((id) =>
  ({ maturity_rating_id: id, component_id: Math.random() })
  )
)

const addGameMaturityRating = () => {
  game_maturity_ratings.value.push({ maturity_rating_id: null, component_id: Math.random() })
}

const deleteGameMaturityRating = (component_id: number) => {
  game_maturity_ratings.value.splice(
    _.findIndex(game_maturity_ratings.value, { 'component_id': component_id }),
    1
  )
}

const onSubmit = async() => {
  const response = await put(props.data.paths.update_game_path, {
    responseKind: "json",
    body: {
      game: {
        ...game.value,
        titles: titles.value.filter((t) => t.text),
        maturity_rating_ids: _.compact(game_maturity_ratings.value.map((t) => t.maturity_rating_id))
      }
    }
  })
  if (response.ok) {
    window.location.reload();
  } else if (response.unprocessableEntity){
    const json = await response.json
    errors.value = json.errors
    nextTick(() =>{
      const elements = document.getElementsByClassName("invalid")
      elements?.[0].scrollIntoView(true)
    })
  } else {
    return alert('登録に失敗しました')
  }
}

</script>

<template lang="pug">
form
  div.modal__body(class="uk-modal-body" uk-overflow-auto)
    table.games__modal-basic
        tr(:class="{ invalid: errors.titles.length }")
          th {{ t("title") }}
          td
            template(v-for="(title, i) in titles" :key="title.component_id")
              TitleForm(
                v-model:title="titles[i]"
                :locales="constants.locales"
                @add-title="addTitle"
                @delete-title="deleteTitle"
                :index="i"
                )
            div.games__modal-basic-add(@click="addTitle")
              span {{ "追加" }} 
            div.games__modal-basic-error(v-if="errors.titles.length")
              span(v-for="message in errors.titles" :key="message")
                | {{ message }}
        ReleaseForm(
          :development_states="constants.development_states"
          :release_date_announces="constants.release_date_announces"
          :release_seasons="constants.release_seasons"
          :software_formats="constants.software_formats"
          v-model:release="game.release"
        )
        tr
          th {{ "プレイヤー数" }}
          td(class="uk-flex")
            div.games__modal-basic-list(class="uk-flex uk-flex-middle" v-for="play_mode in constants.play_modes" :key="play_mode.name")
              input(
                type="checkbox"
                class="uk-checkbox"
                :id="play_mode.name"
                name="play_mode_ids"
                :value="play_mode.id"
                v-model="game.play_mode_ids"
              )
              label(:for="play_mode.name") {{ play_mode.name_i18n }}
        tr
          th {{ "サービス内容" }}
          td(class="uk-flex")
            div.games__modal-basic-list(class="uk-flex uk-flex-middle" v-for="business_model in constants.business_models" :key="business_model.name")
              input(
                  type="radio"
                  class="uk-radio"
                  :id="business_model.name"
                  name="business_model_ids"
                  :value="business_model.id"
                  v-model="game.business_model_id"
                )
              label(:for="business_model.name") {{ business_model.name_i18n }}
        CompanyForm(
          v-model:companies="game.developers"
          :form-label="t('developer')"
          category="developer"
        )
        CompanyForm(
          v-model:companies="game.publishers"
          :form-label="t('publisher')"
          category="publisher"
        )
        tr(:class="{ invalid: errors.game_maturity_ratings.length }")
          th {{  "年齢制限" }}
          td
            template(v-for="(game_maturity_rating, i) in game_maturity_ratings" :key="game_maturity_rating.component_id")
              MaturityRatingForm(
                v-model:game_maturity_rating="game_maturity_ratings[i]"
                :maturity_ratings="constants.maturity_ratings"
                :maturity_rating_systems="constants.maturity_rating_systems"
                @delete-rating="deleteGameMaturityRating"
                :index="i"
              )
            div.games__modal-basic-add(@click="addGameMaturityRating")
              span {{ "追加" }}
            div.games__modal-basic-error(v-if="errors.game_maturity_ratings.length")
              span(v-for="message in errors.game_maturity_ratings" :key="message")
                | {{ message }}

  div.modal__footer(class="uk-modal-footer uk-text-center")
    button(class="uk-button uk-button-default uk-modal-close" type="button")
      | {{ t("close") }}
    button(
      class="uk-button uk-button-primary uk-modal-submit"
      type="button"
      @click="onSubmit"
    )
      | {{ t("save") }}

</template>

<i18n lang="json">{
  "ja": {
    "title": "ゲームタイトル",
    "developer": "デベロッパー",
    "publisher": "パブリッシャー",
    "close": "閉じる",
    "save": "保存"
  }
}</i18n>
